import React from 'react';
import ReactDOM from 'react-dom';
import { 
  AcademicCapIcon, 
  ChartBarIcon, 
  UserGroupIcon, 
  CalendarIcon,
  CheckCircleIcon,
  SparklesIcon,
  BookOpenIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

import api from '../api';

export default function Dashboard({ transcript, joinedClubs: initialJoinedClubs = [], onPageChange }) {
  const [courseCatalog, setCourseCatalog] = React.useState([]);
  const [catalogLoaded, setCatalogLoaded] = React.useState(false);
  const [showGradProgressModal, setShowGradProgressModal] = React.useState(false);
  const [showClubsModal, setShowClubsModal] = React.useState(false);
  const [clubsData, setClubsData] = React.useState([]);
  const [joinedClubs, setJoinedClubs] = React.useState([]);
  const [gradProgress, setGradProgress] = React.useState(0);

  // Fetch course catalog for A-G matching
  React.useEffect(() => {
    const fetchCourseCatalog = async () => {
      try {
        const response = await fetch(`https://dvtime-1c6x.onrender.com/courses`);
        const data = await response.json();
        setCourseCatalog(data);
        setCatalogLoaded(true);
        console.log('Course catalog loaded:', data.length, 'courses');
      } catch (error) {
        console.error('Error fetching course catalog:', error);
        setCatalogLoaded(true); // Still mark as loaded to prevent infinite loading
      }
    };
    fetchCourseCatalog();
  }, []);

  // Fetch clubs data for modal
  React.useEffect(() => {
    const fetchClubsData = async () => {
      try {
        const response = await fetch(`https://dvtime-1c6x.onrender.com/clubs`);
        const data = await response.json();
        setClubsData(data);
      } catch (error) {
        console.error('Error fetching clubs data:', error);
      }
    };
    fetchClubsData();
  }, []);

  // Fetch joined clubs from API
  React.useEffect(() => {
    const fetchJoinedClubs = async () => {
      try {
        const response = await api.get('/user/clubs');
        setJoinedClubs(response.data.joined_clubs || []);
      } catch (error) {
        console.error('Error fetching joined clubs:', error);
        setJoinedClubs([]);
      }
    };
    fetchJoinedClubs();
  }, []);

  // Listen for clubs update event
  React.useEffect(() => {
    const handleClubsUpdate = async () => {
      try {
        const response = await api.get('/user/clubs');
        setJoinedClubs(response.data.joined_clubs || []);
      } catch (error) {
        console.error('Error refreshing joined clubs:', error);
      }
    };

    window.addEventListener('clubsUpdated', handleClubsUpdate);

    return () => {
      window.removeEventListener('clubsUpdated', handleClubsUpdate);
    };
  }, []);

  // Fetch graduation progress
  React.useEffect(() => {
    const fetchGradProgress = async () => {
      try {
        const response = await api.get('/user/graduation-progress');
        setGradProgress(response.data.overall_progress || 0);
      } catch (error) {
        console.error('Error fetching graduation progress:', error);
      }
    };
    fetchGradProgress();
  }, []);

  // Match transcript course name with catalog course to get A-G tag
  const matchCourseToAG = (courseName) => {
    if (!courseName) return null;
    
    // Exclude non-A-G courses
    const lowerName = courseName.toLowerCase();
    if (lowerName.includes('leadership') || lowerName.includes('pe ') || lowerName.includes('physical education') ||
        lowerName.includes('health') || lowerName.includes('tutorial') || lowerName.includes('avid')) {
      return null;
    }
    
    // First try exact catalog match if catalog is loaded
    if (catalogLoaded && courseCatalog.length > 0) {
      const match = courseCatalog.find(c => {
        const catalogName = c.name || c.course_name || c['Course Name'] || '';
        return catalogName.trim() === courseName.trim();
      });
      
      if (match) {
        const agTag = match.ag_tag || match.a_g_requirement || match['A-G Requirement'] || match['A-G'] || match.a_g_tag || match.ag_requirement || null;
        if (agTag && agTag !== 'None' && agTag !== '') {
          return agTag;
        }
      }
    }
    
    // Fallback: Keyword matching for external/non-catalog courses
    
    // A - History/Social Science (exclude psychology and art history)
    if ((lowerName.includes('history') && !lowerName.includes('art')) || 
        lowerName.includes('government') || lowerName.includes('geography') || 
        lowerName.includes('economics') || lowerName.includes('sociology') ||
        lowerName.includes('civics') || lowerName.includes('political')) {
      return 'A';
    }
    
    // B - English
    if (lowerName.includes('english') || lowerName.includes('literature') || lowerName.includes('composition') || 
        lowerName.includes('writing') || lowerName.includes('rhetoric')) {
      return 'B';
    }
    
    // C - Mathematics (exclude programming/computer)
    if ((lowerName.includes('math') && !lowerName.includes('computer')) || 
        lowerName.includes('algebra') || lowerName.includes('geometry') || 
        lowerName.includes('calculus') || lowerName.includes('statistics') || lowerName.includes('trigonometry') ||
        lowerName.includes('precalculus') || lowerName.includes('precalc')) {
      return 'C';
    }
    
    // D - Laboratory Science
    if (lowerName.includes('biology') || lowerName.includes('chemistry') || lowerName.includes('physics') || 
        lowerName.includes('science') || lowerName.includes('anatomy') || lowerName.includes('physiology') ||
        lowerName.includes('environmental')) {
      return 'D';
    }
    
    // E - Language Other Than English
    if (lowerName.includes('spanish') || lowerName.includes('french') || lowerName.includes('german') || 
        lowerName.includes('chinese') || lowerName.includes('japanese') || lowerName.includes('mandarin') ||
        lowerName.includes('latin') || lowerName.includes('italian') || lowerName.includes('korean')) {
      return 'E';
    }
    
    // F - Visual & Performing Arts (includes art history and music theory)
    if (lowerName.includes('art') || lowerName.includes('music') || lowerName.includes('drama') || 
        lowerName.includes('theater') || lowerName.includes('dance') || lowerName.includes('photography') ||
        lowerName.includes('film') || lowerName.includes('visual')) {
      return 'F';
    }
    
    // G - College-Prep Elective (includes psychology, computer programming)
    if (lowerName.includes('psychology') || lowerName.includes('computer') || 
        lowerName.includes('programming') || lowerName.includes('engineering') ||
        lowerName.includes('robotics') || lowerName.includes('coding')) {
      return 'G';
    }
    
    return null;
  };

  // Calculate stats from transcript
  const calculateStats = () => {
    if (!transcript || !transcript.courses) {
      return {
        totalCourses: 0,
        weightedGPA: '0.00',
        unweightedGPA: '0.00',
        agProgress: 0,
        totalClubs: 0
      };
    }

    const courses = transcript.courses;
    console.log('Dashboard - Total courses:', courses.length);
    console.log('Dashboard - Sample course:', courses[0]);
    const totalCourses = courses.length;
    
    // Detect field name format from first course
    const sampleCourse = courses[0] || {};
    const hasUppercaseFields = 'Course' in sampleCourse || 'Mark' in sampleCourse;
    console.log('Using uppercase fields:', hasUppercaseFields);
    console.log('Catalog loaded:', catalogLoaded, 'Catalog size:', courseCatalog.length);
    
    // Get GPA values
    const weightedGPA = transcript.gpa_summary?.cumulative_weighted_gpa || 
                       transcript.gpa_summary?.cumulative_gpa_weighted || 0;
    const unweightedGPA = transcript.gpa_summary?.cumulative_unweighted_gpa || 
                         transcript.gpa_summary?.cumulative_gpa_unweighted || 0;
    
    // Calculate A-G progress based on UC/CSU requirements
    // Requirements are in YEARS (2 semesters = 1 year)
    const agRequirements = {
      'A': { name: 'History/Social Science', required: 2, completed: 0 }, // 2 years (4 semesters)
      'B': { name: 'English', required: 4, completed: 0 },                // 4 years (8 semesters)
      'C': { name: 'Mathematics', required: 3, completed: 0 },            // 3 years (6 semesters)
      'D': { name: 'Laboratory Science', required: 2, completed: 0 },     // 2 years (4 semesters)
      'E': { name: 'Language Other Than English', required: 2, completed: 0 }, // 2 years (4 semesters)
      'F': { name: 'Visual & Performing Arts', required: 1, completed: 0 },    // 1 year (2 semesters)
      'G': { name: 'College-Prep Elective', required: 1, completed: 0 }        // 1 year (2 semesters)
    };

    // Count completed A-G courses (in years)
    // Each course is typically 1 semester (0.5 years), so 2 courses = 1 year
    let agCoursesFound = 0;
    let matchedFromCatalog = 0;
    courses.forEach((course, index) => {
      // Handle both field name formats
      const courseName = hasUppercaseFields ? course.Course : (course.course_name || course['Course Name']);
      const grade = hasUppercaseFields ? course.Mark : (course.grade || course.Grade);
      const credits = hasUppercaseFields ? course.Credits : (course.credits || course.Credits || 5);
      
      // Try to get A-G tag from transcript first, then match with catalog
      let agTag = hasUppercaseFields 
        ? (course['A-G'] || course['A-G Requirement'] || course.a_g_requirement)
        : (course.a_g_requirement || course.a_g_tag || course['A-G Requirement']);
      
      // If no A-G tag in transcript, try to match with course catalog
      if ((!agTag || agTag === 'None' || agTag === '') && catalogLoaded) {
        agTag = matchCourseToAG(courseName);
        if (agTag) {
          matchedFromCatalog++;
        }
      }
      
      if (index < 3) {
        console.log(`Course ${index}:`, {
          name: courseName,
          agTag: agTag,
          matchedFromCatalog: (!course['A-G'] && !course.a_g_requirement && agTag),
          grade: grade,
          credits: credits
        });
      }
      
      if (agTag && agTag !== 'None' && agTag !== '' && agRequirements[agTag]) {
        agCoursesFound++;
        // Only count if grade is C- or better (UC/CSU requirement)
        const passingGrades = ['A+', 'A', 'A-', 'B+', 'B', 'B-', 'C+', 'C', 'C-'];
        if (passingGrades.includes(grade)) {
          // Each semester course = 0.5 years
          // Most high school courses are 1 semester (5 credits)
          const years = credits / 10; // 10 credits = 1 year (2 semesters)
          agRequirements[agTag].completed += years;
        }
      }
    });
    console.log('A-G courses found:', agCoursesFound);
    console.log('Matched from catalog:', matchedFromCatalog);
    console.log('A-G requirements:', agRequirements);

    // Calculate overall progress
    let totalRequired = 0;
    let totalCompleted = 0;
    Object.values(agRequirements).forEach(req => {
      totalRequired += req.required;
      // Don't count more than required for each category
      totalCompleted += Math.min(req.completed, req.required);
    });
    
    return {
      totalCourses,
      weightedGPA: weightedGPA.toFixed(2),
      unweightedGPA: unweightedGPA.toFixed(2),
      totalClubs: joinedClubs.length
    };
  };

  const stats = {
    ...calculateStats(),
    gradProgress
  };

  const StatCard = ({ icon: Icon, title, value, subtitle, color = 'indigo', onClick, clickable = false }) => {
    const colorClasses = {
      indigo: 'text-indigo-700 dark:text-indigo-300 bg-indigo-100 dark:bg-indigo-900/30',
      blue: 'text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30',
      green: 'text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900/30',
      purple: 'text-purple-700 dark:text-purple-300 bg-purple-100 dark:bg-purple-900/30',
      pink: 'text-pink-700 dark:text-pink-300 bg-pink-100 dark:bg-pink-900/30',
      orange: 'text-orange-700 dark:text-orange-300 bg-orange-100 dark:bg-orange-900/30'
    };

    return (
      <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-md border border-gray-200 dark:border-gray-800 hover:shadow-lg dark:hover:shadow-gray-900/50 transition-all duration-200">
        <div className="flex items-center justify-between mb-4">
          <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
            <Icon className="h-6 w-6" />
          </div>
        </div>
        {clickable ? (
          <button onClick={onClick} className="text-left w-full group">
            <h3 className="text-sm font-medium text-purple-600 dark:text-purple-400 mb-1 group-hover:text-purple-700 dark:group-hover:text-purple-300 transition-colors underline decoration-purple-600 dark:decoration-purple-400">{title}</h3>
            <p className="text-3xl font-bold text-gray-900 dark:text-white mb-1">{value}</p>
            {subtitle && (
              <p className="text-xs text-gray-500 dark:text-gray-500">{subtitle}</p>
            )}
          </button>
        ) : (
          <>
            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{title}</h3>
            <p className="text-3xl font-bold text-gray-900 dark:text-white mb-1">{value}</p>
            {subtitle && (
              <p className="text-xs text-gray-500 dark:text-gray-500">{subtitle}</p>
            )}
          </>
        )}
      </div>
    );
  };

  // Check if transcript has data
  const hasTranscriptData = transcript && transcript.courses && transcript.courses.length > 0;

  // Empty state when no transcript
  if (!hasTranscriptData) {
    return (
      <div className="space-y-6">
        {/* Welcome Header */}
        <div className="rounded-2xl p-8 text-white shadow-lg" style={{
          background: 'linear-gradient(to right, var(--accent-500), var(--accent-600), var(--accent-700))'
        }}>
          <h1 className="text-3xl font-bold mb-2">Welcome Back! 👋</h1>
          <p className="text-white/90">Get started by uploading your transcript.</p>
        </div>

        {/* Empty State */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl p-12 shadow-md border border-gray-200 dark:border-gray-800 text-center">
          <div className="mb-6">
            <svg className="h-24 w-24 text-gray-400 dark:text-gray-600 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">No Transcript Found</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
            Upload your transcript to see your academic overview, GPA stats, and A-G progress.
          </p>
          <button
            onClick={() => {
              const event = new CustomEvent('openSettings', { detail: 'transcript' });
              window.dispatchEvent(event);
            }}
            className="px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-semibold rounded-xl hover:from-indigo-600 hover:to-purple-600 transition-all shadow-md hover:shadow-lg"
          >
            Upload Transcript in Settings
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Header - Gradient based on accent color */}
      <div className="rounded-2xl p-8 text-white shadow-lg" style={{
        background: 'linear-gradient(to right, var(--accent-500), var(--accent-600), var(--accent-700))'
      }}>
        <h1 className="text-3xl font-bold mb-2">Welcome Back! 👋</h1>
        <p className="text-white/90">Here's your academic overview at a glance.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <StatCard
          icon={BookOpenIcon}
          title="Total Courses"
          value={stats.totalCourses}
          subtitle="Courses completed"
          color="blue"
        />
        
        <StatCard
          icon={ChartBarIcon}
          title="Weighted GPA"
          value={stats.weightedGPA}
          subtitle="Cumulative (9-12)"
          color="blue"
        />
        
        <StatCard
          icon={ChartBarIcon}
          title="Unweighted GPA"
          value={stats.unweightedGPA}
          subtitle="Cumulative (9-12)"
          color="green"
        />
        
        <StatCard
          icon={CheckCircleIcon}
          title="Graduation Progress"
          value={`${stats.gradProgress}%`}
          subtitle="DVHS requirements"
          color="purple"
          onClick={() => setShowGradProgressModal(true)}
          clickable={true}
        />
        
        <StatCard
          icon={UserGroupIcon}
          title="Clubs Joined"
          value={stats.totalClubs}
          subtitle="Active memberships"
          color="pink"
          onClick={() => setShowClubsModal(true)}
          clickable={true}
        />
        
        <StatCard
          icon={CalendarIcon}
          title="Current Year"
          value={transcript?.student_info?.grade_level || '12'}
          subtitle="Grade level"
          color="orange"
        />
      </div>

      {/* Quick Actions with Tips */}
      <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-md border border-gray-200 dark:border-gray-800">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Quick Actions</h2>
          <span className="text-sm text-gray-900 dark:text-gray-100">
            💡 Tip: Use{' '}
            <button
              onClick={() => onPageChange && onPageChange('transcript')}
              className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 underline font-semibold"
            >
              What If?
            </button>
            {' '}mode to test future grades
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button 
            onClick={() => onPageChange && onPageChange('teachers')}
            className="p-4 rounded-xl border-2 border-blue-200 dark:border-blue-800 hover:border-blue-400 dark:hover:border-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all text-left group"
          >
            <AcademicCapIcon className="h-8 w-8 text-blue-600 dark:text-blue-400 mb-2 group-hover:scale-110 transition-transform" />
            <h3 className="font-semibold text-gray-900 dark:text-white mb-1">View Teachers</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">Browse teacher directory</p>
          </button>
          
          <button 
            onClick={() => onPageChange && onPageChange('courses')}
            className="p-4 rounded-xl border-2 border-purple-200 dark:border-purple-800 hover:border-purple-400 dark:hover:border-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all text-left group"
          >
            <CalendarIcon className="h-8 w-8 text-purple-600 dark:text-purple-400 mb-2 group-hover:scale-110 transition-transform" />
            <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Plan Schedule</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">Build your course plan</p>
          </button>
          
          <button 
            onClick={() => onPageChange && onPageChange('clubs')}
            className="p-4 rounded-xl border-2 border-pink-200 dark:border-pink-800 hover:border-pink-400 dark:hover:border-pink-600 hover:bg-pink-50 dark:hover:bg-pink-900/20 transition-all text-left group"
          >
            <UserGroupIcon className="h-8 w-8 text-pink-600 dark:text-pink-400 mb-2 group-hover:scale-110 transition-transform" />
            <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Explore Clubs</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">Find new clubs to join</p>
          </button>
        </div>
      </div>

      {/* Graduation Progress Modal */}
      {showGradProgressModal && <GraduationProgressModal onClose={() => setShowGradProgressModal(false)} />}
      
      {/* Clubs Modal */}
      {showClubsModal && <ClubsModal joinedClubs={joinedClubs} clubsData={clubsData} onClose={() => setShowClubsModal(false)} onPageChange={onPageChange} />}
    </div>
  );
}

// Graduation Progress Modal Component
function GraduationProgressModal({ onClose }) {
  const [progressData, setProgressData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [editMode, setEditMode] = React.useState(false);
  const [editedCategories, setEditedCategories] = React.useState({});

  React.useEffect(() => {
    const fetchGraduationProgress = async () => {
      try {
        console.log('Fetching graduation progress from:', '/user/graduation-progress');
        const response = await api.get('/user/graduation-progress');
        console.log('✅ Graduation progress response:', response.data);
        console.log('Categories count:', Object.keys(response.data.categories || {}).length);
        console.log('Total credits:', response.data.total_credits);
        console.log('Overall progress:', response.data.overall_progress);
        
        // Log first category with courses
        const firstCategoryWithCourses = Object.entries(response.data.categories || {}).find(([_, data]) => data.courses?.length > 0);
        if (firstCategoryWithCourses) {
          console.log('Sample category with courses:', firstCategoryWithCourses[0], firstCategoryWithCourses[1]);
        }
        
        setProgressData(response.data);
      } catch (error) {
        console.error('❌ Error fetching graduation progress:', error);
        console.error('Error details:', error.response?.data || error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchGraduationProgress();
  }, []);

  React.useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  if (loading) {
    return ReactDOM.createPortal(
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4" onClick={onClose}>
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-4xl w-full p-6" onClick={(e) => e.stopPropagation()}>
          <p className="text-center text-gray-500 dark:text-gray-400">Loading graduation progress...</p>
        </div>
      </div>,
      document.body
    );
  }

  // Always show the modal with requirements, even if no data
  // If backend returns empty categories, initialize them with default requirements
  const defaultCategories = {
    "English": { earned: 0, required: 40, percentage: 0, remaining: 40, completed: false, courses: [] },
    "Health": { earned: 0, required: 5, percentage: 0, remaining: 5, completed: false, courses: [] },
    "Algebra": { earned: 0, required: 10, percentage: 0, remaining: 10, completed: false, courses: [] },
    "General Math": { earned: 0, required: 10, percentage: 0, remaining: 10, completed: false, courses: [] },
    "Life Science": { earned: 0, required: 10, percentage: 0, remaining: 10, completed: false, courses: [] },
    "Physical Science": { earned: 0, required: 10, percentage: 0, remaining: 10, completed: false, courses: [] },
    "World History": { earned: 0, required: 15, percentage: 0, remaining: 15, completed: false, courses: [] },
    "US History": { earned: 0, required: 10, percentage: 0, remaining: 10, completed: false, courses: [] },
    "American Govt": { earned: 0, required: 5, percentage: 0, remaining: 5, completed: false, courses: [] },
    "Economics": { earned: 0, required: 5, percentage: 0, remaining: 5, completed: false, courses: [] },
    "Physical Education": { earned: 0, required: 20, percentage: 0, remaining: 20, completed: false, courses: [] },
    "Electives": { earned: 0, required: 50, percentage: 0, remaining: 50, completed: false, courses: [] },
    "Fine Art": { earned: 0, required: 10, percentage: 0, remaining: 10, completed: false, courses: [] },
    "Voc/Applied Art": { earned: 0, required: 10, percentage: 0, remaining: 10, completed: false, courses: [] },
    "World Language": { earned: 0, required: 10, percentage: 0, remaining: 10, completed: false, courses: [] }
  };
  
  // Use edited categories if in edit mode, otherwise use original data
  const categories = editMode && Object.keys(editedCategories).length > 0
    ? editedCategories
    : (progressData?.categories && Object.keys(progressData.categories).length > 0) 
      ? progressData.categories 
      : defaultCategories;
  
  // Recalculate totals based on current categories
  // Cap each category at its required amount (excess credits don't count toward total)
  const total_credits = Object.values(categories).reduce((sum, cat) => {
    const earned = cat.earned || 0;
    const required = cat.required || 0;
    return sum + Math.min(earned, required);
  }, 0);
  const total_required = progressData?.total_required || 220;
  const overall_progress = total_required > 0 ? Math.min(100, (total_credits / total_required * 100)) : 0;
  const on_track = progressData?.on_track || false;

  const handleEditToggle = () => {
    if (!editMode) {
      // Entering edit mode - copy current categories
      setEditedCategories(JSON.parse(JSON.stringify(categories)));
    }
    setEditMode(!editMode);
  };

  const handleCreditChange = (category, newValue) => {
    const earned = parseFloat(newValue) || 0;
    const required = editedCategories[category].required;
    const percentage = required > 0 ? Math.min(100, (earned / required * 100)) : 0;
    const remaining = Math.max(0, required - earned);
    const completed = earned >= required;

    setEditedCategories({
      ...editedCategories,
      [category]: {
        ...editedCategories[category],
        earned,
        percentage: parseFloat(percentage.toFixed(2)),
        remaining,
        completed
      }
    });
  };

  const handleSaveEdits = async () => {
    try {
      // Save to backend
      await api.post('/user/graduation-progress/override', {
        categories: editedCategories
      });
      
      // Update local state
      setProgressData({
        ...progressData,
        categories: editedCategories
      });
      setEditMode(false);
      
      console.log('✅ Graduation progress overrides saved successfully');
    } catch (error) {
      console.error('❌ Error saving graduation progress overrides:', error);
      alert('Failed to save changes. Please try again.');
    }
  };

  const handleCancelEdits = () => {
    setEditedCategories({});
    setEditMode(false);
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Graduation Requirements</h3>
            <div className="flex items-center gap-2">
              {editMode ? (
                <>
                  <button
                    onClick={handleSaveEdits}
                    className="px-3 py-1.5 text-sm font-semibold bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                  >
                    Save
                  </button>
                  <button
                    onClick={handleCancelEdits}
                    className="px-3 py-1.5 text-sm font-semibold bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  onClick={handleEditToggle}
                  className="px-3 py-1.5 text-sm font-semibold bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                >
                  Edit Credits
                </button>
              )}
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            If something is wrong with the parsed credits, you can manually adjust them with the{' '}
            <span className="font-semibold">Edit Credits</span> button.
          </p>
        </div>

        <div className="p-4 sm:p-6">
          {/* Overall Progress */}
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl p-4 sm:p-6 mb-4 sm:mb-6 border border-purple-200 dark:border-purple-800">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-2">
              <div>
                <h4 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">Overall Progress</h4>
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">{total_credits} of {total_required} credits earned</p>
              </div>
              <div className="text-left sm:text-right">
                <span className="text-3xl sm:text-4xl font-bold text-purple-600 dark:text-purple-400">{overall_progress.toFixed(2)}%</span>
                {on_track && <p className="text-xs sm:text-sm text-green-600 dark:text-green-400 mt-1">✓ On Track</p>}
              </div>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
              <div 
                className="bg-gradient-to-r from-purple-500 to-pink-500 h-3 rounded-full transition-all duration-500"
                style={{ width: `${Math.min(100, overall_progress)}%` }}
              />
            </div>
          </div>

          {/* Requirements Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
            {Object.entries(categories).map(([category, data]) => {
              const percentage = data.percentage || 0;
              const isComplete = data.completed;
              const courses = data.courses || [];
              
              return (
                <div key={category} className={`p-3 sm:p-4 rounded-xl border-2 transition-all ${
                  isComplete 
                    ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700' 
                    : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                }`}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h5 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                        {category}
                        {isComplete && <CheckCircleIcon className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 dark:text-green-400" />}
                      </h5>
                      {editMode ? (
                        <div className="flex items-center gap-2 mt-1">
                          <div className="flex items-center gap-1">
                            <input
                              type="text"
                              inputMode="decimal"
                              value={data.earned}
                              onChange={(e) => handleCreditChange(category, e.target.value)}
                              className="w-20 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            />
                            <div className="flex flex-col divide-y divide-gray-300 dark:divide-gray-600 rounded-md border border-gray-300 dark:border-gray-600 overflow-hidden">
                              <button
                                type="button"
                                onClick={() => handleCreditChange(category, (data.earned + 0.5).toFixed(1))}
                                className="px-1 text-[10px] leading-none bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200"
                              >
                                ▲
                              </button>
                              <button
                                type="button"
                                onClick={() => handleCreditChange(category, Math.max(0, data.earned - 0.5).toFixed(1))}
                                className="px-1 text-[10px] leading-none bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200"
                              >
                                ▼
                              </button>
                            </div>
                          </div>
                          <span className="text-xs text-gray-600 dark:text-gray-400">/ {data.required} credits</span>
                        </div>
                      ) : (
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">
                          {data.earned.toFixed(1)} / {data.required} credits
                        </p>
                      )}
                    </div>
                    <span className={`text-base sm:text-lg font-bold ${
                      isComplete ? 'text-green-600 dark:text-green-400' : 'text-gray-700 dark:text-gray-300'
                    }`}>
                      {Math.min(100, percentage).toFixed(2)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-3">
                    <div 
                      className={`h-2 rounded-full transition-all duration-300 ${
                        isComplete 
                          ? 'bg-green-500' 
                          : 'bg-gradient-to-r from-purple-500 to-pink-500'
                      }`}
                      style={{ width: `${Math.min(100, percentage).toFixed(2)}%` }}
                    />
                  </div>
                  
                  {/* Course List */}
                  {courses.length > 0 && (
                    <div className="mt-3 space-y-1">
                      {courses.map((course, idx) => (
                        <div key={idx} className="flex justify-between items-center text-xs bg-gray-50 dark:bg-gray-900/50 px-2 py-1.5 rounded">
                          <span className="text-gray-700 dark:text-gray-300 flex-1 truncate" title={course.name}>
                            {course.name}
                          </span>
                          <span className="text-gray-500 dark:text-gray-400 ml-2 font-medium">
                            {course.credits} cr
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {data.remaining > 0 && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 font-medium">
                      {data.remaining.toFixed(1)} credits remaining
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          {/* Summary */}
          <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
            <h5 className="font-semibold text-gray-900 dark:text-white mb-2">Summary</h5>
            <div className="grid grid-cols-2 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {Object.values(categories).filter(c => c.completed).length}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">Completed</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-600 dark:text-gray-400">
                  {Object.values(categories).filter(c => !c.completed).length}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">Not Completed</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function AGProgressModal_OLD({ transcript, courseCatalog, matchCourseToAG, onClose }) {
  React.useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  if (!transcript || !transcript.courses) {
    return ReactDOM.createPortal(
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4" onClick={onClose}>
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-2xl w-full p-6" onClick={(e) => e.stopPropagation()}>
          <p className="text-center text-gray-500 dark:text-gray-400">No transcript data available</p>
        </div>
      </div>,
      document.body
    );
  }

  const courses = transcript.courses || [];
  const agRequirements = {
    'A': { name: 'History/Social Science', required: 2, completed: 0, courses: [] },
    'B': { name: 'English', required: 4, completed: 0, courses: [] },
    'C': { name: 'Mathematics', required: 3, completed: 0, courses: [] },
    'D': { name: 'Laboratory Science', required: 2, completed: 0, courses: [] },
    'E': { name: 'Language Other Than English', required: 2, completed: 0, courses: [] },
    'F': { name: 'Visual & Performing Arts', required: 1, completed: 0, courses: [] },
    'G': { name: 'College-Prep Elective', required: 1, completed: 0, courses: [] }
  };

  // Group courses by name to combine semesters
  const courseGroups = {};
  courses.forEach(course => {
    const courseName = course.Course || course.course_name;
    const grade = course.Mark || course.grade;
    const credits = course.Credits || course.credits || 5;
    const term = course.Term || '';
    
    // Match course to catalog to get A-G tag
    const agTag = matchCourseToAG(courseName);
    
    if (agTag && agTag !== 'None' && agTag !== '' && agRequirements[agTag]) {
      const passingGrades = ['A+', 'A', 'A-', 'B+', 'B', 'B-', 'C+', 'C', 'C-'];
      if (passingGrades.includes(grade)) {
        const years = credits / 10;
        agRequirements[agTag].completed += years;
        
        // Group by course name
        if (!courseGroups[agTag]) courseGroups[agTag] = {};
        if (!courseGroups[agTag][courseName]) {
          courseGroups[agTag][courseName] = { grades: [], credits: 0 };
        }
        courseGroups[agTag][courseName].grades.push(grade);
        courseGroups[agTag][courseName].credits += credits;
      }
    }
  });
  
  // Convert grouped courses to display format
  Object.keys(courseGroups).forEach(agTag => {
    Object.entries(courseGroups[agTag]).forEach(([courseName, data]) => {
      agRequirements[agTag].courses.push({
        name: courseName,
        grades: data.grades,
        credits: data.credits
      });
    });
  });

  const totalRequired = Object.values(agRequirements).reduce((sum, req) => sum + req.required, 0);
  const totalCompleted = Object.values(agRequirements).reduce((sum, req) => sum + Math.min(req.completed, req.required), 0);
  const overallProgress = Math.round((totalCompleted / totalRequired) * 100);

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white">A-G Progress Details</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 italic">
            Note: These requirements may not exactly match DVHS records. Please verify with your counselor.
          </p>
        </div>
        
        <div className="p-6">
          <div className="flex flex-col items-center mb-8">
            <div className="relative w-32 h-32">
              <svg className="transform -rotate-90 w-32 h-32">
                <circle cx="64" cy="64" r="56" stroke="currentColor" strokeWidth="8" fill="none" className="text-gray-200 dark:text-gray-700" />
                <circle cx="64" cy="64" r="56" stroke="currentColor" strokeWidth="8" fill="none" strokeDasharray={`${2 * Math.PI * 56}`} strokeDashoffset={`${2 * Math.PI * 56 * (1 - overallProgress / 100)}`} className="text-purple-600 dark:text-purple-400 transition-all duration-1000" />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-3xl font-bold text-gray-900 dark:text-white">{overallProgress}%</span>
              </div>
            </div>
            <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">Overall A-G Progress</p>
            <p className="text-xs text-gray-500 dark:text-gray-500">{totalCompleted.toFixed(1)} of {totalRequired} years completed</p>
          </div>

          <div className="space-y-4">
            {Object.entries(agRequirements).map(([key, req]) => {
              const progress = Math.min((req.completed / req.required) * 100, 100);
              return (
                <div key={key} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white">{key} - {req.name}</h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{req.completed.toFixed(1)} / {req.required} years</p>
                    </div>
                    <span className="text-sm font-bold text-purple-600 dark:text-purple-400">{Math.round(progress)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-2">
                    <div className="bg-purple-600 dark:bg-purple-400 h-2 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
                  </div>
                  {req.courses.length > 0 && (
                    <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                      <p className="font-medium mb-1">Completed courses:</p>
                      <div className="space-y-1">
                        {req.courses.map((course, idx) => {
                          // Split grades into pairs (2 per line for semesters)
                          const gradePairs = [];
                          for (let i = 0; i < course.grades.length; i += 2) {
                            gradePairs.push(course.grades.slice(i, i + 2));
                          }
                          
                          return (
                            <div key={idx} className="py-1 px-2 bg-gray-50 dark:bg-gray-800 rounded">
                              <div className="flex items-center justify-between">
                                <span className="font-medium">{course.name}</span>
                                <div className="text-right">
                                  {gradePairs.map((pair, pairIdx) => (
                                    <div key={pairIdx} className="text-purple-600 dark:text-purple-400 font-semibold">
                                      {pair.join(' • ')}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

// Clubs Modal Component
function ClubsModal({ joinedClubs, clubsData, onClose, onPageChange }) {
  React.useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Get full club details for joined clubs
  const joinedClubDetails = joinedClubs.map(clubName => {
    const club = clubsData.find(c => c['Club Name'] === clubName);
    return club || null;
  }).filter(Boolean);

  return ReactDOM.createPortal(
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4" 
      onClick={onClose}
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
    >
      <div 
        className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col border border-gray-200 dark:border-gray-800" 
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between bg-gradient-to-r from-pink-50 to-purple-50 dark:from-pink-900/20 dark:to-purple-900/20">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-pink-100 dark:bg-pink-900/40 rounded-lg">
              <UserGroupIcon className="h-6 w-6 text-pink-600 dark:text-pink-400" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">My Clubs</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {joinedClubDetails.length} {joinedClubDetails.length === 1 ? 'club' : 'clubs'} joined
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
            aria-label="Close modal"
          >
            <XMarkIcon className="h-6 w-6 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {joinedClubDetails.length === 0 ? (
            <div className="text-center py-12">
              <UserGroupIcon className="h-16 w-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Clubs Joined Yet</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Explore the <button onClick={() => { onClose(); onPageChange && onPageChange('clubs'); }} className="font-semibold hover:underline" style={{ color: 'var(--accent-600)' }}>Clubs</button> page to find and join clubs that interest you!
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {joinedClubDetails.map((club, idx) => (
                <div 
                  key={club['Club Name'] || idx}
                  className="bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-800/50 rounded-xl p-5 border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-all duration-200"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
                        {club['Club Name'] || 'Unknown Club'}
                      </h3>
                    </div>
                    <CheckCircleIcon className="h-6 w-6 text-green-500 flex-shrink-0" />
                  </div>

                  {club['Club Description'] && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">
                      {club['Club Description']}
                    </p>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      {club['Best Point of Contact'] && (
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 bg-indigo-100 dark:bg-indigo-900/40 rounded">
                            <svg className="h-4 w-4 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <a href={`mailto:${club['Best Point of Contact']}`} className="text-indigo-600 dark:text-indigo-400 hover:underline truncate">
                            {club['Best Point of Contact']}
                          </a>
                        </div>
                      )}
                      
                      {club['Meeting Frequency'] && (
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 bg-green-100 dark:bg-green-900/40 rounded">
                            <ClockIcon className="h-4 w-4 text-green-600 dark:text-green-400" />
                          </div>
                          <span className="text-gray-700 dark:text-gray-300 truncate">
                            {club['Meeting Frequency']}
                          </span>
                        </div>
                      )}
                      
                      {club['Advisor '] && (
                        <div className="flex items-center gap-2 col-span-2">
                          <div className="p-1.5 bg-orange-100 dark:bg-orange-900/40 rounded">
                            <svg className="h-4 w-4 text-orange-600 dark:text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                          </div>
                          <span className="text-gray-700 dark:text-gray-300">
                            <span className="font-medium">Advisor:</span> {club['Advisor ']}
                          </span>
                        </div>
                      )}
                      
                      {club['President Name'] && (
                        <div className="flex items-center gap-2 col-span-2">
                          <div className="p-1.5 bg-blue-100 dark:bg-blue-900/40 rounded">
                            <svg className="h-4 w-4 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <span className="text-gray-700 dark:text-gray-300">
                            <span className="font-medium">President:</span> {club['President Name']}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              💡 Tip: Visit the Clubs page to explore more clubs
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-500 text-white font-semibold rounded-lg hover:from-pink-600 hover:to-purple-600 transition-all shadow-md hover:shadow-lg"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
